import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { shouldSuppressRateLimit } from '../utils/httpErrors';

const ConfirmEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    const confirmEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Token di conferma non valido');
        return;
      }

      try {
        // 🔒 SICUREZZA: Usa query parameter invece di path parameter per evitare problemi con caratteri speciali
        const encodedToken = encodeURIComponent(token);
        const response = await axios.get(`${API_BASE_URL}/auth/confirm-email?token=${encodedToken}`);
        setStatus('success');
        setMessage(response.data.message || 'Email confermata con successo!');
      } catch (error) {
        if (shouldSuppressRateLimit(error)) {
          setStatus('error');
          setMessage('Errore durante la conferma dell\'email');
          return;
        }
        // 🔒 MIGLIORAMENTO: Se il token è già stato utilizzato, mostra un messaggio positivo
        const errorMessage = error.response?.data?.detail || 'Errore durante la conferma dell\'email';
        
        // Se il messaggio indica che il token è già stato utilizzato, mostra successo
        if (errorMessage.includes('già utilizzato') || errorMessage.includes('già confermato')) {
          setStatus('success');
          setMessage('La tua email è già stata confermata! Puoi effettuare il login.');
        } else {
          setStatus('error');
          setMessage(errorMessage);
        }
      }
    };

    confirmEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-16 w-16 text-primary-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Conferma in corso...</h1>
            <p className="text-gray-600">Stiamo verificando il tuo token di conferma</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Email confermata!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg"
            >
              Vai al Login
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Errore</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg"
              >
                Vai al Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-all"
              >
                Registrati di nuovo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmailPage;

